import React, { useMemo } from 'react';
import { Card, Col, Row, Statistic, theme } from 'antd';
import { RiseOutlined, StarFilled, ThunderboltOutlined, TrophyOutlined } from '@ant-design/icons';
import type { Lead } from '@/hooks/useLeads';
import { useCurrency } from '@/contexts/CurrencyContext';
import { leadAnnualRevenue } from './leads.constants';

type Props = {
  leads: Lead[];
};

export const LeadsKpiStrip: React.FC<Props> = ({ leads }) => {
  const { token } = theme.useToken();
  const { formatCurrency } = useCurrency();

  const stats = useMemo(() => {
    const totalValue = leads.reduce((sum, l) => sum + leadAnnualRevenue(l), 0);
    const hot = leads.filter((l) => l.priority === 'High' || l.priority === 'Critical').length;
    const qualified = leads.filter((l) => l.lead_status === 'Qualified').length;
    const conversionPct = leads.length > 0 ? Math.round((qualified / leads.length) * 100) : 0;
    return { totalValue, hot, qualified, conversionPct };
  }, [leads]);

  const cardStyle: React.CSSProperties = {
    borderRadius: token.borderRadiusLG,
    boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.04)',
    height: '100%',
  };

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
      <Col xs={24} sm={12} lg={6}>
        <Card style={cardStyle} styles={{ body: { padding: '16px 20px' } }}>
          <Statistic
            title="Visible leads"
            value={leads.length}
            prefix={<ThunderboltOutlined style={{ color: token.colorPrimary }} />}
            valueStyle={{ color: token.colorPrimary, fontWeight: 600 }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card style={cardStyle} styles={{ body: { padding: '16px 20px' } }}>
          <Statistic
            title="Pipeline value (annual)"
            value={stats.totalValue}
            formatter={(v) => formatCurrency(Number(v))}
            prefix={<RiseOutlined style={{ color: token.colorSuccess }} />}
            valueStyle={{ color: token.colorSuccess, fontWeight: 600, fontSize: 20 }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card style={cardStyle} styles={{ body: { padding: '16px 20px' } }}>
          <Statistic
            title="High / critical priority"
            value={stats.hot}
            prefix={<StarFilled style={{ color: token.colorError }} />}
            valueStyle={{ color: token.colorError, fontWeight: 600 }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={3}>
        <Card style={cardStyle} styles={{ body: { padding: '16px 20px' } }}>
          <Statistic
            title="Qualified"
            value={stats.qualified}
            prefix={<TrophyOutlined style={{ color: token.colorSuccess }} />}
            valueStyle={{ color: token.colorSuccess, fontWeight: 600 }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={3}>
        <Card style={cardStyle} styles={{ body: { padding: '16px 20px' } }}>
          <Statistic
            title="Conversion"
            value={stats.conversionPct}
            suffix="%"
            valueStyle={{ fontWeight: 600 }}
          />
        </Card>
      </Col>
    </Row>
  );
};
